import {
  DataTablePagination,
  DataTableSearch,
  Typography,
  DataTable,
  Button,
  Form,
} from '@ui/components';
import { VehicleTableDataDto } from '@interfaces/models';
import { HasPermission } from '@shared/components';
import { useTranslation } from 'react-i18next';
import { Filters } from '@ui/components/icons';
import { useCallback, useState } from 'react';

import { BulkEditCostCenterConfirmationDialog } from '../bulk-edit-cost-center-confirmation-dialog/bulk-edit-cost-center-confirmation-dialog';
import { BulkRemoveVehicleDialog } from '../bulk-remove-vehicle-dialog/bulk-remove-vehicle-dialog';
import { VEHICLES_TABLE_FILTERS_DEFAULT_VALUES, useVehiclesTable } from './use-vehicles-table';
import { VehiclesTableFilters } from '../vehicles-table-filters/vehicles-table-filters';
import { EditVehicleDrawer } from '../edit-vehicle-drawer/edit-vehicle-drawer';
import { RemoveVehicleDialog } from '../remove-vehicle-dialog';
import { CostCenterMultiselect } from './const-center-picker';

export const VehiclesTable = () => {
  const { t } = useTranslation();

  const [removeVehicleDialogOpen, setRemoveVehicleDialogOpen] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleTableDataDto | null>(null);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [bulkRemoveVehicleDialogOpen, setBulkRemoveVehicleDialogOpen] = useState(false);

  const [isBulkEditCostCenterDialogOpen, setIsBulkEditCostCenterDialogOpen] = useState(false);
  const [pendingCostCenters, setPendingCostCenters] = useState<string[]>([]);

  const {
    setSelectedCostCenters,
    bulkEditCostCenters,
    selectedCostCenters,
    costCenterOptions,
    selectedRowsIds,
    filtersOptions,
    columnsLength,
    globalFilter,
    refetchTable,
    filtersFrom,
    totalItems,
    isLoading,
    table,
  } = useVehiclesTable({
    onEditVehicle: (vehicle) => {
      setSelectedVehicle(vehicle);
    },
    onRemoveVehicle: (vehicle) => {
      setRemoveVehicleDialogOpen(vehicle.id);
    },
    onBulkEditVehiclesSuccess: () => {
      setIsBulkEditCostCenterDialogOpen(false);
      setPendingCostCenters([]);
    },
  });

  const handleClearSelection = useCallback(() => {
    table.setRowSelection({});
  }, [table]);

  const hasSelectedRows = selectedRowsIds.length > 0;

  return (
    <div className="flex flex-col bg-table-background rounded-lg overflow-hidden">
      <div className="flex items-center px-4 py-3">
        <Typography variant="labelMediumMedium">
          {hasSelectedRows
            ? t('VEHICLES.TABLE_SELECTED_VEHICLES', {
                postProcess: 'interval',
                count: selectedRowsIds.length,
              })
            : t('VEHICLES.TABLE_TOTAL_VEHICLES', { count: totalItems })}
        </Typography>

        <div className="ml-auto flex items-center gap-4">
          <DataTableSearch
            placeholder={t('VEHICLES.TABLE_SEARCH_PLACEHOLDER')}
            onChange={table.setGlobalFilter}
            value={globalFilter}
          />

          <Button onClick={() => setIsFiltersOpen((current) => !current)} variant="ghost">
            <Filters />
            {t('VEHICLES.TABLE_FILTERS')}
          </Button>

          <DataTablePagination
            pageIndex={table.getState().pagination.pageIndex}
            pageSize={table.getState().pagination.pageSize}
            prevPageDisabled={!table.getCanPreviousPage()}
            nextPageDisabled={!table.getCanNextPage()}
            prevPage={() => table.previousPage()}
            nextPage={() => table.nextPage()}
            totalRows={totalItems}
          />
        </div>
      </div>
      {isFiltersOpen && (
        <Form {...filtersFrom}>
          <VehiclesTableFilters
            reset={() => filtersFrom.reset(VEHICLES_TABLE_FILTERS_DEFAULT_VALUES)}
            controls={filtersFrom.control}
            className="px-4 pb-3 pt-1.5"
            options={filtersOptions}
          />
        </Form>
      )}

      <HasPermission permissionFeature="VEHICLES_EDIT_VEHICLE" permissionType="WRITE">
        {hasSelectedRows && (
          <div className="flex items-center px-2 py-1">
            {costCenterOptions?.length > 1 && (
              <CostCenterMultiselect
                onChange={(value) => {
                  setPendingCostCenters(value);
                  setIsBulkEditCostCenterDialogOpen(true);
                }}
                buttonLabel={t('VEHICLES.TABLE_BULK_EDIT_COST_CENTERS')}
                selectionChange={setSelectedCostCenters}
                options={costCenterOptions ?? []}
                value={selectedCostCenters}
              >
                <Button className="w-auto" variant="ghost">
                  {t('VEHICLES.TABLE_CHANGE_COST_CENTERS')}
                </Button>
              </CostCenterMultiselect>
            )}
            <Button onClick={() => setBulkRemoveVehicleDialogOpen(true)} variant="ghost">
              {t('VEHICLES.TABLE_BULK_DELETE', {
                postProcess: 'interval',
                count: selectedRowsIds.length,
              })}
            </Button>
          </div>
        )}
      </HasPermission>

      <DataTable
        noResultsComponent={
          <Typography variant="labelMediumBook">{t('VEHICLES.TABLE_NO_RESULTS')}</Typography>
        }
        className="h-full overflow-auto"
        columnsLength={columnsLength}
        isLoading={isLoading}
        table={table}
      />

      <RemoveVehicleDialog
        onClose={({ handleSubmit }) => {
          if (handleSubmit) {
            refetchTable();
          }
          setSelectedVehicle(null);
          setRemoveVehicleDialogOpen(null);
        }}
        isOpenForId={removeVehicleDialogOpen}
      />

      {hasSelectedRows && (
        <BulkRemoveVehicleDialog
          onClose={({ handleSubmit }) => {
            if (handleSubmit) {
              refetchTable();
              handleClearSelection();
            }
            setBulkRemoveVehicleDialogOpen(false);
          }}
          isOpen={bulkRemoveVehicleDialogOpen}
          ids={selectedRowsIds}
        />
      )}

      {selectedVehicle && (
        <EditVehicleDrawer
          onClose={({ handleSuccess }) => {
            if (handleSuccess) {
              refetchTable();
            }
            setSelectedVehicle(null);
          }}
          onRemove={() => {
            if (selectedVehicle) {
              setRemoveVehicleDialogOpen(selectedVehicle.id);
            }
          }}
          vehicle={selectedVehicle}
        />
      )}

      <BulkEditCostCenterConfirmationDialog
        setIsOpen={(isOpen) => {
          setIsBulkEditCostCenterDialogOpen(isOpen);
          if (!isOpen) {
            setPendingCostCenters([]);
          }
        }}
        onConfirm={() => {
          bulkEditCostCenters(pendingCostCenters);
          setPendingCostCenters([]);
        }}
        isOpen={isBulkEditCostCenterDialogOpen}
      />
    </div>
  );
};
